import { useAuthorisation } from '@/composables/authorisation'
import { ProductType } from '@/enums'
import { useAuthStore } from '@/pinia/auth'
import { useBasketStore } from '@/pinia/basket'
import { useStyleStore } from '@/pinia/style'
import { useVisualiserStore } from '@/pinia/visualiser'

import alertbox from '@/utils/alertbox'

const routes = [
  {
    path: '/business',
    redirect: '/'
  },
  {
    path: '/',
    name: 'Admin Dashboard',
    meta: { active: 'home' },
    component: () => import('@/views/Business/Dashboard.vue')
  },
  {
    path: '/all-orders',
    name: 'All Orders',
    meta: { active: 'all-orders', showSearch: true },
    component: () => import('@/views/Business/AllOrders.vue'),
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore()
      if (authStore.isExtruderOrExpress) {
        next('/')
      } else if (authStore.branches?.length > 0 && !to.query.branchFilter) {
        next({
          ...to,
          query: {
            branchFilter: authStore.branches.find((x) => x.isPrimary).branchCompanyId
          }
        })
      } else {
        next()
      }
    }
  },
  {
    path: '/manage-news',
    name: 'Manage News',
    meta: { showSearch: true, active: 'manage-news' },
    component: () => import('@/views/Business/ManageNews.vue')
  },
  {
    path: '/manage-news/edit/:id',
    name: 'Edit News',
    meta: { active: 'manage-news' },
    component: () => import('@/views/Business/ManageNews/Edit.vue')
  },
  {
    path: '/manage-news/category',
    name: 'Manage News Categories',
    meta: { active: 'manage-news' },
    component: () => import('@/views/Business/ManageNews/Category.vue')
  },
  {
    path: '/manage-marketing',
    name: 'Manage Marketing',
    meta: { showSearch: true, active: 'marketing' },
    component: () => import('@/views/Business/Marketing.vue')
  },
  {
    path: '/manage-downloads/category',
    name: 'Manage Download Categories',
    meta: { active: 'marketing' },
    component: () => import('@/views/Business/MarketingCategory.vue')
  },
  {
    path: '/customer',
    redirect: '/customers'
  },
  {
    path: '/customers',
    component: () => import('@/views/Business/Customers.vue'),
    redirect: '/customers/portal',
    children: [
      {
        path: 'portal',
        name: 'Customers',
        meta: { showSearch: true, active: 'customer' },
        component: () => import('@/views/Business/Customers/CustomersList.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.branches?.length > 0 && !to.query.branchFilter) {
            next({
              ...to,
              query: {
                branchFilter: authStore.branches.find((x) => x.isPrimary).branchCompanyId
              }
            })
          } else {
            next()
          }
        }
      },
      {
        path: 'lead-capture-mode',
        name: 'Lead Capture Customers',
        meta: { showSearch: true, active: 'customer' },
        component: () => import('@/views/Business/Customers/LeadCapture.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.admin && !authStore.isExtruderOrExpress) {
            next()
          } else {
            next('/')
          }
        }
      },
      {
        path: 'registration-requests',
        name: 'Registration Requests',
        meta: { showSearch: true, active: 'customer' },
        component: () => import('@/views/Business/Customers/RegistrationRequests.vue'),
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.admin && !authStore.isExpress) {
            next()
          } else {
            next('/')
          }
        }
      },
      {
        path: 'analytics',
        name: 'Analytics',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customers/Analytics.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          const { hasFullAccess } = useAuthorisation()
          if (hasFullAccess.value && !authStore.isExtruderOrExpress) {
            next()
          } else {
            next('/customers/portal')
          }
        }
      }
    ]
  },
  {
    path: '/customer/:customerId',
    name: 'Customer',
    meta: { active: 'customer' },
    component: () => import('@/views/Business/Customer.vue'),
    redirect: (to) => {
      const { customerId } = to.params
      return { path: `/customer/${customerId}/details` }
    },
    children: [
      {
        path: '/customer/:customerId/details',
        name: 'Customer Details',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customer/CustomerDetails.vue')
      },
      {
        path: '/customer/:customerId/sales-dashboard',
        name: 'Customer Sales Dashboard',
        meta: { active: 'sales-dashboard', showSearch: true },
        component: () => import('@/views/Business/Customer/SalesDashboard.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isExtruderOrExpress) {
            const { customerId } = to.params
            next(`/customer/${customerId}`)
          } else if (authStore.branches?.length > 0 && !to.query.branchFilter) {
            next({
              ...to,
              query: {
                branchFilter: authStore.branches.find((x) => x.isPrimary).branchCompanyId
              }
            })
          } else {
            next()
          }
        }
      },
      {
        path: '/customer/:customerId/sales-dashboard/new-quote',
        name: 'Customer New Quote',
        meta: { active: 'sales-dashboard' },
        component: () => import('@/views/Shared/NewQuote.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isExtruderOrExpress) {
            const { customerId } = to.params
            next(`/customer/${customerId}`)
          } else {
            next()
          }
        }
      },
      {
        path: '/customer/:customerId/sales-dashboard/new-order',
        name: 'Customer New Order',
        meta: { active: 'sales-dashboard' },
        component: () => import('@/views/Shared/NewOrder.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isExtruderOrExpress) {
            const { customerId } = to.params
            next(`/customer/${customerId}`)
          } else {
            next()
          }
        }
      },
      {
        path: '/customer/:customerId/sales-dashboard/new-test',
        name: 'Customer New Test',
        meta: { active: 'sales-dashboard' },
        component: () => import('@/views/Business/NewTest.vue')
      },
      {
        path: '/customer/:customerId/sales-dashboard/design/:id',
        name: 'Customer Designer',
        meta: { active: 'designer', isProcessing: true },
        component: () => import('@/views/Business/Designer.vue'),
        beforeEnter: (to, from, next) => {
          const basketStore = useBasketStore()
          if (basketStore.hasContract) {
            if (!basketStore.getContractIdentifier?.basketQty) {
              const { customerId } = to.params
              next(`/customer/${customerId}/sales-dashboard/choose-product`)
            } else {
              next()
            }
          } else {
            next(`/customer/${basketStore.customerId}/sales-dashboard/new-quote`)
          }
        }
      },
      {
        path: '/customer/:customerId/sales-dashboard/checkout',
        name: 'Customer Checkout',
        meta: { active: 'designer', isProcessing: true },
        component: () => import('@/views/Business/Checkout.vue'),
        beforeEnter: (to, from, next) => {
          const basketStore = useBasketStore()
          if (basketStore.hasContract) {
            next()
          } else {
            next(`/customer/${basketStore.customerId}/sales-dashboard/new-quote`)
          }
        }
      },
      {
        path: '/customer/:customerId/sales-dashboard/basket',
        name: 'Customer Basket',
        meta: { active: 'designer', isProcessing: true },
        component: () => import('@/views/Business/Basket.vue'),
        beforeEnter: (to, from, next) => {
          const basketStore = useBasketStore()
          if (basketStore.hasContract) {
            next()
          } else {
            next(`/customer/${basketStore.customerId}/sales-dashboard/new-quote`)
          }
        }
      },
      {
        path: '/customer/:customerId/sales-dashboard/choose-product',
        name: 'Customer Choose Product',
        meta: { active: 'sales-dashboard', isProcessing: true },
        component: () => import('@/views/Business/ChooseProduct.vue'),
        beforeEnter: (to, from, next) => {
          const basketStore = useBasketStore()
          if (basketStore.hasContract) {
            next()
          } else {
            next(`/customer/${basketStore.customerId}/sales-dashboard/new-quote`)
          }
        }
      },
      {
        path: '/customer/:customerId/sales-dashboard/choose-extras',
        name: 'Customer Choose Extras',
        meta: { active: 'sales-dashboard', isProcessing: true },
        component: () => import('@/views/Business/ChooseExtras.vue'),
        beforeEnter: (to, from, next) => {
          const basketStore = useBasketStore()
          if (basketStore.hasContract) {
            next()
          } else {
            next(`/customer/${basketStore.customerId}/sales-dashboard/new-quote`)
          }
        }
      },
      {
        path: '/customer/:customerId/sales-dashboard/switch-design/:id/:component',
        name: 'Customer Switch Bay',
        meta: { active: 'designer', isProcessing: true },
        component: () => import('@/views/Business/SwitchDesign.vue'),
        beforeEnter: (to, from, next) => {
          const basketStore = useBasketStore()
          if (basketStore.hasContract) {
            next()
          } else {
            next(`/customer/${basketStore.customerId}/sales-dashboard/new-quote`)
          }
        }
      },
      {
        path: '/customer/:customerId/sales-dashboard/order/:id/:jobId?',
        name: 'Customer Sales Order',
        meta: { active: 'sales-dashboard-view-order' },
        component: () => import('@/views/Business/Order.vue')
      },
      {
        path: '/customer/:customerId/sales-dashboard/quote/:id/:jobId?',
        name: 'Customer Sales Quote',
        meta: { active: 'sales-dashboard-view-quote' },
        component: () => import('@/views/Business/Quote.vue')
      },
      {
        path: '/customer/:customerId/addresses',
        name: 'Customer Manage Addresses',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customer/DeliveryAddresses.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isExtruderOrExpress) {
            next(`/customer/${to.params.customerId}/details`)
          } else {
            next()
          }
        }
      },
      {
        path: '/customer/:customerId/staff',
        name: 'Customer Manage Staff',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customer/Staff.vue')
      },
      {
        path: '/customer/:customerId/vouchers',
        name: 'Customer Vouchers',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customer/Vouchers.vue'),
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.admin && !authStore.isExtruderOrExpress) {
            next()
          } else {
            next(`/customer/${to.params.customerId}/details`)
          }
        }
      },
      {
        path: '/customer/:customerId/discounts',
        name: 'Customer Discounts',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customer/Discounts.vue'),
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.admin && !authStore.isExtruderOrExpress) {
            next()
          } else {
            next(`/customer/${to.params.customerId}/details`)
          }
        }
      },
      {
        path: '/customer/:customerId/processing-options',
        name: 'Customer Processing Options',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customer/ProcessingOptions.vue')
      },
      {
        path: '/customer/:customerId/pricing',
        name: 'Customer Pricing',
        meta: { active: 'customer', showSearch: true },
        component: () => import('@/views/Business/Customer/Pricing.vue'),
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.admin && !authStore.isExtruderOrExpress) {
            next()
          } else {
            next(`/customer/${to.params.customerId}/details`)
          }
        }
      },
      {
        path: '/customer/:customerId/parts-list',
        name: 'Customer Parts List',
        meta: { active: 'customer', showSearch: true },
        component: () => import('@/views/Business/Customer/PartsList.vue'),
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.admin) {
            next()
          } else {
            next(`/customer/${to.params.customerId}/details`)
          }
        }
      },
      {
        path: '/customer/:customerId/product-defaults',
        name: 'Customer Product Defaults',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customer/ProductDefaults.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isExtruderOrExpress) {
            next(`/customer/${to.params.customerId}/details`)
          } else {
            next()
          }
        }
      },
      {
        path: '/customer/:customerId/product-availability',
        name: 'Customer Product Availability',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customer/ProductAvailability.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isExtruderOrExpress) {
            next(`/customer/${to.params.customerId}/details`)
          } else {
            next()
          }
        }
      },
      {
        path: '/customer/:customerId/analytics',
        name: 'Customer Analytics',
        meta: { active: 'customer' },
        component: () => import('@/views/Business/Customer/Analytics.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          const { hasFullAccess } = useAuthorisation()
          if (hasFullAccess.value && !authStore.isExtruderOrExpress) {
            next()
          } else {
            next(`/customer/${to.params.customerId}/details`)
          }
        }
      },
      {
        path: '/customer/:customerId/visualiser',
        name: 'Visualiser Setup',
        meta: { active: 'customer', isProcessing: true },
        component: () => import('@/views/Shared/VisualiserUpload.vue'),
        beforeEnter: (to, from, next) => {
          const basketStore = useBasketStore()
          if (basketStore.hasContract) {
            next()
          } else {
            next(`/customer/${basketStore.customerId}/sales-dashboard/new-quote`)
          }
        }
      },
      {
        path: '/customer/:customerId/visualiser/place',
        name: 'Visualiser',
        meta: { active: 'customer', isProcessing: true },
        component: () => import('@/views/Shared/Visualiser/VisualiserPlace.vue'),
        beforeEnter: (to, from, next) => {
          const basketStore = useBasketStore()
          if (basketStore.hasContract) {
            const visualiserStore = useVisualiserStore()
            if (visualiserStore.images.length > 0 && visualiserStore.selectedImageId) {
              next()
            } else {
              next('/visualiser')
            }
          } else {
            next(`/customer/${basketStore.customerId}/sales-dashboard/new-quote`)
          }
        }
      }
    ]
  },
  {
    path: '/dealer-network',
    redirect: '/enquiries'
  },
  {
    path: '/enquiries',
    name: 'Enquiries',
    meta: { active: 'enquiries', showSearch: true },
    component: () => import('@/views/Business/Enquiries.vue')
  },
  {
    path: '/enquiries/assign/:id',
    name: 'Enquiries Assign',
    meta: { active: 'enquiries-assign' },
    component: () => import('@/views/Business/Enquiries/Assign.vue')
  },
  {
    path: '/enquiries-assigned',
    name: 'Enquiries Assigned',
    meta: { active: 'enquiries-assigned', showSearch: true },
    component: () => import('@/views/Business/Enquiries/Assigned.vue')
  },
  {
    path: '/enquiries-assigned-statuses/:id',
    name: 'Enquiries Assigned Statuses',
    meta: { active: 'enquiries-assigned-statuses' },
    component: () => import('@/views/Business/Enquiries/AssignedStatuses.vue')
  },
  {
    path: '/onboarding',
    redirect: { path: '/setup/payment-gateway/stripe', query: { fromStripe: 'true' } },
    meta: { active: 'payment-gateway' }
  },
  {
    path: '/onboarding/:pathMatch(.*)*',
    redirect: { path: '/setup/payment-gateway/stripe', query: { fromStripe: 'true' } },
    meta: { active: 'payment-gateway' }
  },
  {
    path: '/dealer-network-setup',
    redirect: '/setup/dealer'
  },
  {
    path: '/setup/dealer',
    redirect: '/setup/lead-capture'
  },
  {
    path: '/setup',
    component: () => import('@/views/Business/Setup.vue'),
    redirect: '/',
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore()
      if (authStore.admin) {
        next()
      } else {
        await alertbox({
          title: 'Redirected Home',
          text: 'Admin users only'
        })
        next('/')
      }
    },
    children: [
      {
        path: 'account',
        name: 'Account',
        meta: { active: 'account', root: 'setup' },
        component: () => import('@/views/Business/Setup/Account.vue')
      },
      {
        path: 'staff',
        name: 'Staff',
        meta: { active: 'staff', root: 'setup', showSearch: true },
        component: () => import('@/views/Business/Setup/Staff.vue')
      },
      {
        path: 'styling',
        name: 'Styling',
        meta: { active: 'styling', root: 'setup' },
        component: () => import('@/views/Business/Setup/Styling.vue')
      },
      {
        path: 'costs-prices',
        name: 'Costs Prices',
        meta: { active: 'costs-prices', root: 'setup' },
        component: () => import('@/views/Business/Setup/CostsPrices.vue'),
        redirect: '/setup/costs-prices/financial-categories',
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isExtruderOrExpress) {
            next('/')
          } else {
            next()
          }
        },
        children: [
          {
            path: 'pricing-setup',
            name: 'Pricing Setup',
            meta: { active: 'costs-prices', tab: 'pricing-setup', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/PricingSetup.vue')
          },
          {
            path: 'financial-categories',
            name: 'Financial Categories',
            meta: { active: 'costs-prices', tab: 'financial-categories', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/FinancialCategories.vue')
          },
          {
            path: 'discount-categories',
            name: 'Discounts Categories',
            meta: { active: 'costs-prices', tab: 'discount-categories', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/DiscountCategories.vue')
          },
          {
            path: 'parts-list',
            name: 'Parts List',
            meta: { active: 'costs-prices', tab: 'parts-list', root: 'setup', showSearch: true },
            component: () => import('@/views/Business/Setup/CostsPrices/PartsList.vue')
          },
          {
            path: 'glass',
            name: 'Glass',
            meta: { active: 'costs-prices', tab: 'glass', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/Glass.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.superuser) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'panel',
            name: 'Panel',
            meta: { active: 'costs-prices', tab: 'panel', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/Panel.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.superuser) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'slab-glass-bands',
            name: 'Slab Glass Bands',
            meta: { active: 'costs-prices', tab: 'slab-glass-bands', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/SlabGlassBands.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.superuser) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'slab',
            name: 'Slab',
            meta: { active: 'costs-prices', tab: 'slab', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/Slab.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.superuser) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'labour',
            name: 'Labour',
            meta: { active: 'costs-prices', tab: 'labour', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/Labour.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.superuser) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'product',
            name: 'Product',
            meta: { active: 'costs-prices', tab: 'product', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/Product.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.superuser) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'cost-list',
            name: 'Cost List',
            meta: { active: 'costs-prices', tab: 'cost-list', root: 'setup', showSearch: true },
            component: () => import('@/views/Business/Setup/CostsPrices/CostList.vue')
          },
          {
            path: 'price-list',
            name: 'Price List',
            meta: { active: 'costs-prices', tab: 'price-list', root: 'setup', showSearch: true },
            component: () => import('@/views/Business/Setup/CostsPrices/PriceList.vue')
          },
          {
            path: 'non-stock-extras',
            name: 'Non Stock Extras',
            meta: { active: 'costs-prices', tab: 'non-stock-extras', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/NonStockExtras.vue')
          }
        ]
      },
      {
        path: 'product-settings',
        name: 'Product Settings',
        meta: { active: 'product-settings', root: 'setup' },
        component: () => import('@/views/Business/Setup/ProductSettings.vue'),
        redirect: '/setup/product-settings/general',
        children: [
          {
            path: 'general',
            name: 'General',
            meta: { active: 'product-settings', tab: 'general', root: 'setup' },
            component: () => import('@/views/Business/Setup/ProductSettings/General.vue')
          },
          {
            path: 'selections',
            name: 'Selections',
            meta: { active: 'product-settings', tab: 'selections', root: 'setup' },
            component: () => import('@/views/Business/Setup/ProductSettings/Selections.vue')
          },
          {
            path: 'alternate-glass-clearance',
            name: 'Alternate Glass Clearance',
            meta: { active: 'product-settings', tab: 'alternate-glass-clearance', root: 'setup' },
            component: () => import('@/views/Business/Setup/ProductSettings/AlternateGlassClearance.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (!authStore.isExtruderOrExpress && authStore.superuser) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'alternate-sash-overlaps',
            name: 'Alternate Sash Overlaps',
            meta: { active: 'product-settings', tab: 'alternate-sash-overlaps', root: 'setup' },
            component: () => import('@/views/Business/Setup/ProductSettings/AlternateSashOverlaps.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (!authStore.isExtruderOrExpress && authStore.superuser) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'specifications',
            name: 'Specifications',
            meta: { active: 'product-settings', tab: 'specifications', root: 'setup' },
            component: () => import('@/views/Business/Setup/ProductSettings/Specification.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (!authStore.isExtruderOrExpress || (authStore.isExtruder && authStore.superuser)) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'defaults',
            name: 'Product Defaults',
            meta: { active: 'product-settings', tab: 'defaults', root: 'setup' },
            component: () => import('@/views/Business/Setup/ProductSettings/ProductDefaults.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (!authStore.isExpress) {
                next()
              } else {
                next('/')
              }
            }
          }
        ]
      },
      {
        path: ':mode(lead-capture)',
        name: 'Lead Capture',
        meta: { active: 'lead-capture', root: 'setup' },
        component: () => import('@/views/Business/Setup/Retail.vue'),
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore()
          const { isLeadCaptureAdmin } = useAuthorisation()
          if (isLeadCaptureAdmin.value) {
            next()
          } else {
            await alertbox({
              title: 'Redirected Home',
              text: authStore.admin ? 'No license found' : 'Admin users only'
            })
            next('/')
          }
        },
        children: [
          {
            path: '',
            name: 'Lead Capture Dashboard',
            meta: { tab: 'dashboard', root: 'setup' },
            component: () => import('@/views/Business/Setup/Retail/Dashboard.vue')
          },
          {
            path: 'company',
            name: 'Lead Capture Company Settings',
            meta: { tab: 'company', root: 'setup' },
            component: () => import('@/views/Business/Setup/Retail/Company.vue')
          },
          {
            path: 'products',
            name: 'Lead Capture Products',
            meta: { tab: 'products', root: 'setup' },
            component: () => import('@/views/Business/Setup/Retail/ProductAvailability.vue')
          },
          {
            path: 'styling',
            name: 'Lead Capture Styling',
            meta: { tab: 'styling', root: 'setup' },
            component: () => import('@/views/Business/Setup/Retail/Styling.vue')
          },
          {
            path: 'deployment',
            name: 'Lead Capture Deployment',
            meta: { tab: 'deployment', root: 'setup' },
            component: () => import('@/views/Business/Setup/Retail/Deployment.vue')
          }
        ]
      },
      {
        path: 'delivery',
        name: 'Delivery',
        meta: { active: 'delivery', root: 'setup' },
        component: () => import('@/views/Business/Setup/Delivery.vue'),
        redirect: '/setup/delivery/charges',
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isExtruderOrExpress) {
            next('/')
          } else {
            next()
          }
        },
        children: [
          {
            path: 'charges',
            name: 'Charges',
            meta: { active: 'delivery', tab: 'charges', root: 'setup' },
            component: () => import('@/views/Business/Setup/Delivery/Charges.vue')
          },
          {
            path: 'options',
            name: 'Options',
            meta: { active: 'delivery', tab: 'options', root: 'setup', showSearch: true },
            component: () => import('@/views/Business/Setup/Delivery/Options.vue')
          },
          {
            path: 'lead-times',
            name: 'Lead Times',
            meta: { active: 'delivery', tab: 'lead-times', root: 'setup', showSearch: true },
            component: () => import('@/views/Business/Setup/Delivery/LeadTimes.vue')
          }
        ]
      },
      {
        path: 'payment-gateway',
        name: 'Payment Gateway',
        meta: { active: 'payment-gateway', root: 'setup' },
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore()
          if (!authStore.isPayment) {
            next('/')
            return
          }

          if (to.path === '/setup/payment-gateway') {
            const styleStore = useStyleStore()
            if (styleStore.fabricator && styleStore.fabricator.name === 'Virtuoso') {
              next('/setup/payment-gateway/firstdata')
            } else {
              next('/setup/payment-gateway/stripe')
            }
          } else {
            next()
          }
        },
        children: [
          {
            path: 'stripe',
            name: 'Stripe Account',
            meta: { active: 'payment-gateway', tab: 'account', root: 'setup' },
            component: () => import('@/views/Business/Setup/PaymentGateway/Stripe.vue')
          },
          {
            path: 'firstdata',
            name: 'First Data',
            meta: { active: 'payment-gateway', tab: 'account', root: 'setup' },
            component: () => import('@/views/Business/Setup/PaymentGateway/FirstData.vue')
          }
        ]
      },
      {
        name: 'Retail Option Control',
        path: 'retail-option-control',
        component: () => import('@/views/Business/Setup/RetailOptionControl.vue'),
        meta: { active: 'retail-option-control', root: 'setup' },
        beforeEnter: async (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isExtruderOrExpress) {
            next('/')
          } else {
            next()
          }
        }
      }
    ]
  },
  {
    path: '/branch',
    redirect: '/branches'
  },
  {
    path: '/branches',
    name: 'Branches',
    meta: { showSearch: true, active: 'branch' },
    component: () => import('@/views/Business/Branches.vue'),
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore()
      if (!authStore.licenses.some((x) => x.type === ProductType.BRANCHES)) {
        next('/')
      } else if (authStore.admin) {
        next()
      } else {
        await alertbox({
          title: 'Redirected Home',
          text: 'Admin users only'
        })
        next('/')
      }
    }
  },
  {
    path: '/branch/:id',
    name: 'Branch',
    meta: { active: 'branch' },
    component: () => import('@/views/Business/Branch.vue'),
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore()
      if (!authStore.licenses.some((x) => x.type === ProductType.BRANCHES)) {
        next('/')
      } else if (authStore.admin) {
        next()
      } else {
        await alertbox({
          title: 'Redirected Home',
          text: 'Admin users only'
        })
        next('/')
      }
    },
    redirect: (to) => {
      const { id } = to.params
      return { path: `/branch/${id}/details` }
    },
    children: [
      {
        path: 'details',
        name: 'Branch Details',
        component: () => import('@/views/Business/Branch/Details.vue')
      },
      {
        path: 'staff',
        name: 'Branch Staff',
        component: () => import('@/views/Business/Branch/Staff.vue')
      },
      {
        path: 'address',
        name: 'Branch Address',
        component: () => import('@/views/Business/Branch/Address.vue')
      }
    ]
  },
  {
    path: '/touch-store-admin',
    name: 'QUOTILA Store Admin',
    meta: { active: 'touch-store' },
    component: () => import('@/views/Business/TouchStore.vue'),
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore()
      if (!authStore.isExtruderOrExpress) {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    path: '/marketing',
    name: 'Downloads',
    meta: { showSearch: true, active: 'marketing' },
    component: () => import('@/views/Shared/Marketing.vue')
  },
  {
    path: '/news',
    name: 'News',
    meta: { showSearch: true, active: 'news' },
    component: () => import('@/views/Shared/NewsPosts.vue')
  },
  {
    path: '/news/category/:categoryId',
    name: 'News Category',
    meta: { active: 'news' },
    component: () => import('@/views/Shared/NewsPosts.vue')
  },
  {
    path: '/news/:id',
    name: 'News Post',
    meta: { active: 'news' },
    component: () => import('@/views/Shared/News/NewsPost.vue')
  }
]

export default routes.map((route) => {
  const meta = { ...route.meta, app: 'staff' }
  return { ...route, meta }
})
